// Passive connectivity-level detector for WIN Field v2 (ADR-0110, Card 1/4:
// "Network health module (observe-only)"). Observes the outcome/duration of
// requests the app is already making (via observedFetch and recordSample) and
// infers a three-level connectivity state from a rolling evidence window.
//
// OBSERVE-ONLY: this card adds no behavioural branch anywhere else in the app.
// field:online must report identically to today at every level -- see
// currentLevel() below, which defers to it for the Offline boundary rather
// than computing Offline itself.
//
// New leaf module, alongside field_sync_shared.js -- no imports from
// queue/sync modules, so queue/sync modules can import *this* one without a
// cycle (the "one implementation, not two" convention this codebase already
// uses for field_sync_shared.js).
//
// SW-context safety: field_upload_sync.js and field_image_queue.js (wrapped
// below via observedFetch) are imported directly by field_service_worker.js
// for Background Sync replay, so this module runs there too -- a context with
// no `window` and no synchronous `localStorage`. Every access to either is
// guarded; `typeof window === "undefined"` never throws, even for an
// undeclared identifier, so these guards are safe in both contexts.
// Consequence (accepted, per the card's investigation): Background-Sync
// replay evidence never reaches the health signal -- foreground page traffic
// is the dominant source and is what the Card 3 degraded-mode UX cares about.

import { classifyQuality, isPoorOrWorse, isGoodOrFair } from "./pwa_network_quality"

export const LEVEL_OFFLINE  = 0
export const LEVEL_DEGRADED = 1
export const LEVEL_ONLINE   = 2

// Hysteresis is asymmetric (ADR-0110): quick to enter Degraded, slow to leave
// it. A single slow request must never flip the whole app in and out of a
// mode -- flapping reads as a broken app.
const DEGRADED_EXIT_DWELL_MS = 30000

// Rolling evidence window: the last 10 samples OR all samples from the last
// 60 seconds, whichever set is larger. No persistence across reloads -- a
// fresh window on load is acceptable for v1 (spec §1).
const WINDOW_MAX_SAMPLES = 10
const WINDOW_MAX_AGE_MS  = 60000

const ONLINE_STORAGE_KEY = "field:online"
const HEALTH_STORAGE_KEY = "field:health"
const HEALTH_CHANGED_EVENT = "field:health-changed"

// -- module state (one instance per page load / per SW execution context) --

let _level = LEVEL_ONLINE
let _enteredDegradedAt = null
let _evidence = [] // chronological: { kind, durationMs, outcome, sizeClass, quality, recordedAt }

// -- environment guards --

function _hasWindow() {
  return typeof window !== "undefined"
}

function _hasLocalStorage() {
  return typeof localStorage !== "undefined"
}

function _now() {
  return typeof performance !== "undefined" ? performance.now() : Date.now()
}

// -- public API --

// Wrap any fetch that should feed the health evidence stream. Behaves exactly
// like fetch() -- same signature, same return, same throw semantics. Records
// the outcome into the evidence window as a side effect; never changes what
// the caller sees.
export async function observedFetch(url, options, { kind, sizeClass = "small" } = {}) {
  const startedAt = _now()
  let response
  try {
    response = await fetch(url, options)
  } catch (error) {
    recordSample({
      kind,
      durationMs: Math.round(_now() - startedAt),
      outcome: _isTimeoutError(error) ? "timeout" : "network_error",
      sizeClass
    })
    throw error
  }

  recordSample({
    kind,
    durationMs: Math.round(_now() - startedAt),
    outcome: _outcomeForResponse(response),
    sizeClass
  })
  return response
}

// Record a sample manually (used by pwa/network_status_controller's ping
// poll via the field-specific subclass hook, which has its own fetch call
// already and doesn't need to route through observedFetch).
export function recordSample({ kind, durationMs, outcome, sizeClass = "small" }) {
  const now = Date.now()
  _evidence.push({
    kind,
    durationMs: durationMs ?? null,
    outcome,
    sizeClass,
    quality: _sampleQuality(durationMs, outcome),
    recordedAt: now
  })
  _trimEvidence(now)
  _evaluateTransition(now)
}

// Current level: 0 | 1 | 2. Synchronous, reads from the in-memory evidence
// window. Offline is NOT computed by this module -- network_status_controller
// remains the sole authority on the confirmed online/offline boundary
// (NIC events / failed ping), via the existing field:online flag. This
// module only adds the Degraded layer on top of "online" (ADR-0110).
export function currentLevel() {
  if (_hasLocalStorage() && localStorage.getItem(ONLINE_STORAGE_KEY) === "false") {
    return LEVEL_OFFLINE
  }
  if (_backgroundLevel !== null) return Math.min(_backgroundLevel, _level)
  return _level
}

// Card #3865 -- the service worker has no field:online flag, so its uploads
// always ran at Online timeouts (60s a photo) even when Background Sync fired
// on a connection that was barely there. Its sync handler probes the server
// once it holds the sync lock and states the level it found here just before
// the uploads start, for the length of the pass; null clears it (the handler's
// finally). Page code never calls this.
let _backgroundLevel = null

export function setBackgroundLevel(level) {
  _backgroundLevel = level ?? null
}

// Read-only snapshot of the current evidence window, most recent last. For
// display only (e.g. the diagnostics page) -- not part of the hysteresis API.
export function evidenceSnapshot() {
  return _evidence.map((sample) => ({ ...sample }))
}

// Most recent ping-kind sample (the periodic poll in
// pwa/network_status_controller.js's _poll, fed in here via
// field_network_status_controller.js's _recordPingSample), or null if none
// has landed yet. Card #4671 / ADR-0174 decision 2 -- this IS the "small
// periodic probe" the iPhone/webkit connection-class estimate in
// lib/field_navigation.js is built from: webkit exposes no Network
// Information API, so there is no browser-reported effectiveType/rtt/downlink
// to fall back on there, only our own timing.
export function lastPingSample() {
  const pings = _pingSamples()
  return pings.length ? { ...pings[pings.length - 1] } : null
}

// Exposes observedFetch's own outcome classification for callers that can't
// route a request through observedFetch itself (Card #3492) -- field_image_queue.js's
// foreground XHR upload path (lib/xhr_upload.js's uploadWithProgress) still
// needs to feed the SAME connectivity-evidence stream a plain fetch() would
// have, since image uploads are a meaningfully large slice of that evidence
// (ADR-0110). classifyFetchOutcome accepts anything response-shaped (.ok,
// .status) -- the XHR path's Response-like wrapper qualifies without change.
export { _outcomeForResponse as classifyFetchOutcome, _isTimeoutError as isTimeoutError }

// -- field:online integration (page context only) --
//
// On confirmed recovery from Offline, start fresh rather than resume a stale
// pre-offline Degraded judgement -- "EXIT OFFLINE -> ONLINE... then
// immediately re-evaluate Degraded-vs-Online using the same rules above"
// (spec §2). currentLevel() already defers to the field:online flag while
// offline, so this listener only matters for what happens on the way back.
if (_hasWindow()) {
  window.addEventListener(ONLINE_STORAGE_KEY, () => {
    _level = LEVEL_ONLINE
    _enteredDegradedAt = null
    _evidence = []
  })
}

// -- private: sample classification --

function _outcomeForResponse(response) {
  if (response.ok) return "ok"
  if (response.status >= 500) return "server_error"
  // 4xx: the round trip succeeded and the server answered -- a validation or
  // auth failure is not a connectivity signal, so treat it as "ok" for the
  // purpose of quality classification (duration still counts as evidence).
  return "ok"
}

function _isTimeoutError(error) {
  return error?.name === "TimeoutError" || error?.name === "AbortError"
}

// A single sample's quality band, via the shared classifier. A non-"ok"
// outcome (timeout/network_error/server_error) is a confirmed-bad round trip
// -- classified the same way classifyQuality treats "no response" (avgMs
// null), i.e. Very poor, regardless of how long it took to fail.
function _sampleQuality(durationMs, outcome) {
  if (outcome !== "ok") return classifyQuality(null, 100)
  return classifyQuality(durationMs, 0)
}

// -- private: evidence window --

function _trimEvidence(now) {
  const within60s = _evidence.filter((sample) => now - sample.recordedAt <= WINDOW_MAX_AGE_MS)
  const last10    = _evidence.slice(-WINDOW_MAX_SAMPLES)
  _evidence = within60s.length > last10.length ? within60s : last10
}

// Large image uploads are excluded from latency-trend calculations (ADR-0110)
// -- a slow multi-MB upload on decent 4G is not evidence of a bad connection.
function _nonLargeSamples() {
  return _evidence.filter((sample) => sample.sizeClass !== "large")
}

function _realTrafficSamples() {
  return _nonLargeSamples().filter((sample) => sample.kind !== "ping")
}

function _pingSamples() {
  return _evidence.filter((sample) => sample.kind === "ping")
}

function _lastNPoorOrWorse(samples, n) {
  if (samples.length < n) return false
  return samples.slice(-n).every((sample) => isPoorOrWorse(sample.quality))
}

function _lastNGoodOrFair(samples, n) {
  if (samples.length < n) return false
  return samples.slice(-n).every((sample) => isGoodOrFair(sample.quality))
}

function _countPoorOrWorseInLastN(samples, n) {
  return samples.slice(-n).filter((sample) => isPoorOrWorse(sample.quality)).length
}

// -- private: hysteresis state machine --

function _shouldEnterDegraded() {
  if (_lastNPoorOrWorse(_realTrafficSamples(), 2)) return true
  if (_countPoorOrWorseInLastN(_nonLargeSamples(), 5) >= 3) return true
  if (_lastNPoorOrWorse(_pingSamples(), 2)) return true
  return false
}

function _shouldExitDegraded(now) {
  if (_enteredDegradedAt == null) return false
  if (now - _enteredDegradedAt < DEGRADED_EXIT_DWELL_MS) return false
  return _lastNGoodOrFair(_nonLargeSamples(), 2)
}

function _evaluateTransition(now) {
  if (_level === LEVEL_ONLINE && _shouldEnterDegraded()) {
    _transition(LEVEL_DEGRADED, now)
  } else if (_level === LEVEL_DEGRADED && _shouldExitDegraded(now)) {
    _transition(LEVEL_ONLINE, now)
  }
}

// Reset on level change: a level, once reached, is judged on fresh evidence,
// not the evidence that caused the previous transition (spec §2).
function _transition(newLevel, now) {
  const previousLevel = _level
  _level = newLevel
  _evidence = []
  _enteredDegradedAt = newLevel === LEVEL_DEGRADED ? now : null

  _writeStoredLevel(newLevel)
  _dispatchHealthChanged(newLevel, previousLevel)
}

function _writeStoredLevel(level) {
  if (!_hasLocalStorage()) return
  try {
    localStorage.setItem(HEALTH_STORAGE_KEY, String(level))
  } catch { /* storage unavailable -- observation only, never fatal */ }
}

function _dispatchHealthChanged(level, previousLevel) {
  if (!_hasWindow()) return
  try {
    window.dispatchEvent(new CustomEvent(HEALTH_CHANGED_EVENT, { detail: { level, previousLevel } }))
  } catch { /* no-op in a context without CustomEvent */ }
}
